import React, { useEffect } from "react";
import { useNotification } from "../contexts/NotificationContext";
import { trackUpsellInteraction } from "../api/auth";

const SubscribeUpsellModal = ({
  open,
  product = "chat",
  clientId = "",
  onClose,
  onStart, 
}) => {
  const { showInfo } = useNotification();
  
  // Track when modal is viewed
  useEffect(() => {
    if (open) {
      trackUpsellInteraction('viewed', product, null, `Upsell modal opened for ${product}`)
        .catch(err => console.error('Failed to track view:', err));
    }
  }, [open, product]);
  
  if (!open) return null;

  const headline = product === "voice" ? "Never Miss a Call Again" : "Unlock Chat Support";
  const blurb =
    product === "voice"
      ? "Guests who prefer calling won't wait for chat. Capture those reservations with an AI agent that answers 24/7."
      : "Deliver fast, friendly help right on your site. Enable 24/7 chat with guided flows, escalation, and rich analytics.";

  const handleStart = async () => {
    // Track "started_trial" action
    try {
      await trackUpsellInteraction('started_trial', product, null, `User expressed interest in ${product} feature`);
    } catch (err) {
      console.error('Failed to track trial start:', err);
    }
    
    if (typeof onStart === "function") {
      onStart({ product, clientId });
    } else {
      showInfo(
        'Request Received', 
        'Thank you for your interest. A member of our team will contact you shortly.',
        0 // Don't auto-dismiss - requires manual close
      );
    }
  };

  const handleRemindLater = async () => {
    // Track "remind_later" action
    try {
      await trackUpsellInteraction('remind_later', product, null, `User clicked remind me later for ${product}`);
    } catch (err) {
      console.error('Failed to track remind later:', err);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-10 w-full max-w-3xl text-center border dark:border-gray-800">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-2 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
          aria-label="Close"
          type="button"
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>

        {/* Icon/Illustration */}
        {product === "voice" ? (
          <div className="flex justify-center mb-6">
            <div className="relative">
              {/* Phone icon */}
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#32B1FF] to-blue-600 flex items-center justify-center shadow-lg">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              {/* Sound waves */}
              <div className="absolute -right-8 top-1/2 -translate-y-1/2 flex gap-1">
                <div className="w-1 h-4 bg-[#32B1FF] rounded-full animate-pulse"></div>
                <div className="w-1 h-6 bg-[#32B1FF] rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-1 h-4 bg-[#32B1FF] rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#32B1FF] to-blue-600 flex items-center justify-center shadow-lg">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </div>
          </div>
        )}

        <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2">{headline}</h1>
        <p className="text-gray-600 dark:text-gray-300 mb-6">{blurb}</p>

        <div className="grid sm:grid-cols-3 gap-4 text-left mb-8">
          <div className="rounded-xl border dark:border-gray-700 p-4">
            <div className="font-semibold text-gray-800 dark:text-gray-200 mb-1">
              {product === "voice" ? "Easy Integration" : "Quick Setup"}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {product === "voice" ? "Forward your calls to us. That's the whole setup." : "Connect in minutes. No heavy lifting."}
            </div>
          </div>
          <div className="rounded-xl border dark:border-gray-700 p-4">
            <div className="font-semibold text-gray-800 dark:text-gray-200 mb-1">
              {product === "voice" ? "No Extra Staff Needed" : "Scales with You"}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {product === "voice" ? "Handles 1 call or 100 during peak season." : "From pilot to production, we've got you."}
            </div>
          </div>
          <div className="rounded-xl border dark:border-gray-700 p-4">
            <div className="font-semibold text-gray-800 dark:text-gray-200 mb-1">
              {product === "voice" ? "Full Transcripts" : "Actionable Insights"}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {product === "voice" ? "Read every call without listening to recordings." : "Dashboards and transcripts to learn & iterate."}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 justify-center">
          <button
            className="px-6 py-3 rounded-lg bg-[#32B1FF] text-white font-semibold hover:bg-[#259ae6] transition-colors"
            onClick={handleStart}
            type="button"
          >
            Try Now
          </button>
          <button
            className="px-6 py-3 rounded-lg bg-white dark:bg-gray-800 border dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            onClick={handleRemindLater}
            type="button"
          >
            Remind me later
          </button>
        </div>

       
      </div>
    </div>
  );
};

export default SubscribeUpsellModal;
